import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  ToggleButtonGroup,
  ToggleButton,
  FormControlLabel,
  Checkbox,
  Button,
  Chip,
  Tabs,
  Tab,
  Badge,
} from "@mui/material";

const roles = ["PALLEGGIO", "BANDA", "CENTRALE", "LIBERO", "OPPOSTO"];


export default function MatchLineupTab({ state, dispatch }: any) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<any>(null);
  const [selectedSet, setSelectedSet] = useState<number | null>(null);
  const [role, setRole] = useState("");
  const [starter, setStarter] = useState(false);

  const [tabSet, setTabSet] = useState(0);

  const openRoleDialog = (player: any, setNumber: number) => {
    setSelectedPlayer(player);
    setSelectedSet(setNumber);
    setRole("");
    setStarter(true);
    setDialogOpen(true);
  };

  const confirmRole = () => {
    if (!selectedPlayer || selectedSet == null) return;

    dispatch({
      type: "ADD_PLAYER_TO_SET",
      playerId: selectedPlayer.id,
      setNumber: selectedSet,
      role,
      starter,
    });

    setDialogOpen(false);
  };

  const currentSet = state.sets[tabSet];

  // funzione di supporto: quante lineups complete
  const isSetComplete = (set: any) => {
    const participants = state.participants.filter((p: any) => p.setNumber === set.setNumber);
    const starters = participants.filter((p: any) => p.starter);
    return starters.length >= 6; // esempio: squadra completa = 6 starter
  };

  return (
    <Box sx={{ display: "flex", height: "100%" }}>
      {/* Tabs verticali dei set */}
      <Tabs
        orientation="vertical"
        variant="scrollable"
        value={tabSet}
        onChange={(e, val) => setTabSet(val)}
        sx={{ borderRight: 1, borderColor: "divider", minWidth: 130 }}
      >
        {state.sets.map((s: any, idx: number) => {
          const complete = isSetComplete(s);
          const score = `[${s.teamScore}-${s.opponentScore}]`;

          return (
            <Tab
              key={s.setNumber}
              label={
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pr: 1 }}>
                  <Typography sx={{ fontWeight: tabSet === idx ? "bold" : "normal" }}>
                    Set {s.setNumber}
                  </Typography>
                  <Badge
                    badgeContent={complete ? "⚡" : score}
                    color={complete ? "success" : "default"}
                    sx={{ ml: 3 }}
                  />
                </Box>
              }
            />
          );
        })}
      </Tabs>

      {/* Contenuto del set selezionato */}
      <Box sx={{ flex: 1, p: 2 }}>
        {currentSet && (
          <>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Set {currentSet.setNumber} - Score: {currentSet.teamScore} : {currentSet.opponentScore}
            </Typography>

            {/* Selected players */}
            <Box sx={{ mb: 2 }}>
              {state.participants
                .filter((p: any) => p.setNumber === currentSet.setNumber)
                .map((p: any, idx: number) => {
                  const pl = state.players.find((x: any) => x.id === p.playerId) || {};
                  const nickname = pl.nickname ?? "Unknown";
                  const shirt = pl.shirtNumber ?? "-";
                  const gender = pl.gender ?? "M";
                  const shortRole = p.role?.substring(0, 3).toUpperCase();
                  const icon = p.starter ? "⭐" : "⬆️";
                  const chipColor = gender === "F" ? "secondary" : "primary";

                  return (
                    <Chip
                      key={idx}
                      label={`${icon} [${shirt}] ${nickname} (${shortRole})`}
                      onDelete={() =>
                        dispatch({
                          type: "REMOVE_PLAYER",
                          playerId: p.playerId,
                          setNumber: currentSet.setNumber,
                        })
                      }
                      sx={{ mr: 1, mb: 1 }}
                      color={chipColor}
                      variant="outlined"
                    />
                  );
                })}
            </Box>

            {/* Available players */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Available players
            </Typography>
            <Grid container spacing={2}>
              {state.players.map((pl: any) => {
                const alreadyInSet = state.participants.some(
                  (p: any) =>
                    p.playerId === pl.id &&
                    p.setNumber === currentSet.setNumber
                );
                const gender = pl.gender ?? "M";
                const avatarColor = gender === "F" ? "#ffebf0" : "#e3f2fd";

                return (
                  <Grid size={{ xs: 6, sm: 4, md: 3, lg: 2 }} key={pl.id}>
                    <Card
                      onDoubleClick={() =>
                        !alreadyInSet && openRoleDialog(pl, currentSet.setNumber)
                      }
                      sx={{
                        p: 1,
                        textAlign: "center",
                        cursor: alreadyInSet ? "not-allowed" : "pointer",
                        opacity: alreadyInSet ? 0.4 : 1,
                        borderRadius: 2,
                        border: "1px solid #ccc",
                        "&:hover": {
                          boxShadow: alreadyInSet ? 0 : 3,
                        },
                      }}
                    >
                      <Avatar
                        sx={{
                          mx: "auto",
                          mb: 1,
                          bgcolor: avatarColor,
                          color: "#000",
                        }}
                      >
                        {pl.shirtNumber}
                      </Avatar>
                      <Typography variant="subtitle2">{pl.nickname}</Typography>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}
      </Box>

      {/* Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Select Role for {selectedPlayer?.nickname}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <ToggleButtonGroup
            exclusive
            value={role}
            onChange={(e, val) => val && setRole(val)}
            sx={{ mb: 2, flexWrap: "wrap" }}
          >
            {roles.map((r) => (
              <ToggleButton key={r} value={r} sx={{ m: 0.5 }}>
                {r.substring(0, 3).toUpperCase()}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <FormControlLabel
            control={
              <Checkbox
                checked={starter}
                onChange={(e) => setStarter(e.target.checked)}
              />
            }
            label="Starter"
          />

          <Box textAlign="right" mt={2}>
            <Button variant="contained" disabled={!role} onClick={confirmRole}>
              Confirm
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
